import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { z } from 'zod';
import type { TtsResponse, TtsSettings } from './types';
import styles from './admin.module.css';
import { MAX_REFERENCE_BYTES } from '../mimo/contracts';

const limits = [
  ['timeoutMs', '超时（毫秒）', 1000, 300000],
  ['maxTextChars', '文本最大字符数', 1, 20000],
  ['maxReferenceBytes', '参考音频上限（字节）', 1024, MAX_REFERENCE_BYTES],
  ['temporaryTtlSeconds', '临时音频有效期（秒）', 30, 3600],
  ['maxDailyRequests', '每日请求上限', 1, 10000],
] as const;
const modeLabels = { preset: '预设', design: '设计', clone: '复刻' } as const;
const settingsSchema = z.object({
  enabled: z.boolean(),
  baseUrl: z.string().url().refine((value) => {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash;
  }),
  models: z.object({ preset: z.string().min(1).max(100), design: z.string().min(1).max(100), clone: z.string().min(1).max(100) }),
  timeoutMs: z.number().int().min(1000).max(300000),
  maxTextChars: z.number().int().min(1).max(20000),
  maxReferenceBytes: z.number().int().min(1024).max(MAX_REFERENCE_BYTES),
  temporaryTtlSeconds: z.number().int().min(30).max(3600),
  maxDailyRequests: z.number().int().min(1).max(10000),
});

export function TtsForm({
  initial,
  busy,
  onSave,
}: {
  initial: TtsResponse;
  busy: boolean;
  onSave: (settings: TtsSettings, apiKey?: string) => Promise<void>;
}): ReactElement {
  const [settings, setSettings] = useState(initial.settings);
  const [apiKey, setApiKey] = useState('');
  const [invalid, setInvalid] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const result = settingsSchema.safeParse(settings);
    if (!result.success || apiKey.length > 1000 || (apiKey !== '' && !apiKey.trim())) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    const key = apiKey || undefined;
    setApiKey('');
    await onSave(result.data, key);
  }

  return (
    <form className={styles.ttsForm} onSubmit={(event) => void submit(event)}>
      <fieldset disabled={busy} className={styles.fields}>
        <legend>语音服务</legend>
        <label className={styles.check}><input type="checkbox" checked={settings.enabled} onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })} />启用服务</label>
        <label className={styles.field}><span>HTTPS 接口地址</span><input type="url" value={settings.baseUrl} required onChange={(event) => setSettings({ ...settings, baseUrl: event.target.value })} /></label>
        <label className={styles.field}><span>API Key（{initial.apiKeyConfigured ? '已配置' : '未配置'}）</span><input type="password" autoComplete="new-password" value={apiKey} maxLength={1000} onChange={(event) => setApiKey(event.target.value)} /></label>
        {(['preset', 'design', 'clone'] as const).map((mode) => (
          <label key={mode} className={styles.field}>
            <span>{modeLabels[mode]}模型</span>
            <input aria-label={`${modeLabels[mode]}模型`} value={settings.models[mode]} required maxLength={100} onChange={(event) => setSettings({ ...settings, models: { ...settings.models, [mode]: event.target.value } })} />
          </label>
        ))}
        {limits.map(([name, label, min, max]) => (
          <label key={name} className={styles.field}>
            <span>{label}</span>
            <input type="number" required step={1} min={min} max={max} value={Number.isNaN(settings[name]) ? '' : settings[name]} onChange={(event) => setSettings({ ...settings, [name]: event.target.valueAsNumber })} />
          </label>
        ))}
      </fieldset>
      <p className={styles.scopeHint}>语音套餐已并入「套餐」分区：管理员在套餐能力中开启“自动语音”即可授权 TTS。</p>
      {invalid && <p role="alert" className={styles.error}>请检查语音服务配置项。</p>}
      <button type="submit" disabled={busy} className={styles.primary}>{busy ? '保存中…' : '保存 TTS 设置'}</button>
    </form>
  );
}
